/** Shared status-line mapper for the `/selection-edit/stream` SSE vocabulary,
 *  used by both the highlight-edit flow (useHighlightEditFlow) and the Revise
 *  flow (useProposalReviewFlow). Kept in one place so the two surfaces stay in
 *  parity and the mapping is unit-testable.
 *
 *  The stream is untyped on the wire (a bag of `type` + payload keys). The key
 *  detail: on the DEFAULT agent-loop path, `retrieve_from_library` emits
 *  `task_start` with the natural-language query under `need` (NOT `title`), and
 *  streams `retrieval_step` (pre-formatted `message`), `task_done` (via/level),
 *  and `task_failed` events. Surfacing those tells the writer WHAT is being
 *  looked up instead of a generic "Retrieving reference…". */

/** Trim a query/message to keep the single status line readable. Mirrors
 *  sseAdapter's truncateForSubline (kept local to avoid importing the run
 *  adapter into the edit-highlight hooks). */
function truncate(s: string, max = 64): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/** Map one selection-edit SSE event to a short status label, or null when the
 *  event is too granular to surface (the last meaningful label then sticks).
 *  ``draftingFallback`` lets each caller phrase the final "rewriting" step for
 *  its own surface (a highlighted span vs. a paragraph). */
export function labelForSelectionEditEvent(
  event: Record<string, unknown>,
  draftingFallback = "Rewriting the highlighted text…",
): string | null {
  const type = String(event.type ?? "");
  switch (type) {
    case "status":
      return typeof event.label === "string" && event.label ? event.label : "Checking what to look up…";
    case "plan_ready": {
      const total = Number(event.total_tasks ?? 0);
      if (total <= 0) return "No lookups needed — rewriting…";
      return `Looking up ${total} reference${total === 1 ? "" : "s"}…`;
    }
    case "task_start": {
      // Agent-loop path carries the query under `need`; the non-agent /
      // drafting-grade paths carry it under `title`.
      const query =
        (typeof event.need === "string" && event.need) ||
        (typeof event.title === "string" && event.title) ||
        "";
      return query ? `Retrieving: ${truncate(query)}` : "Retrieving reference…";
    }
    case "retrieval_step": {
      // The server pre-formats a human `message` (e.g. "Searching sources: …",
      // "3 result(s) for: …", or a find_section/find_table cascade step);
      // fall back to `hint`. Surface it as-is so web-search progress shows.
      const msg =
        (typeof event.message === "string" && event.message) ||
        (typeof event.hint === "string" && event.hint) ||
        "";
      return msg ? truncate(msg) : null;
    }
    case "task_done": {
      const via = String(event.via ?? "");
      if (!via || via === "cache") return null; // cache hits are noise
      const level = typeof event.level === "string" && event.level ? ` (${event.level})` : "";
      return `Found in library${level}`;
    }
    case "task_failed":
      return "Looking elsewhere…";
    case "drafting_response":
      return typeof event.message === "string" && event.message ? event.message : draftingFallback;
    default:
      return null;
  }
}
